import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Box, Grid, makeStyles, Tooltip, Typography } from "@material-ui/core";

import {
  getYear,
  getData,
  currencyFormatter,
  formatDatetime,
  nFormatter,
} from "../../../../services";
import { EmptyPage } from "../../../../components/parts";
import { COLORS } from "../../../../theme/colors";
import { Table } from "../../../../components/table";
import { TableFilter } from "../../../../components/parts";
import EditIcon from "../../../../assets/images/icons/edit.svg";
import { Pagination } from "../../../../components/pagination";
import SearchIcon from "../../../../assets/images/icons/search.svg";
import { Icon, UnderscoreTextInput } from "../../../../components/base";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { ANY_FILTER_KEY, ITEMS_PER_PAGE, STATUS_MAP } from "../../constants";
import { TENANTS_FILTERS } from "../../../../services/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  searchContainer: {
    marginBottom: theme.spacing(4),
  },
  searchBtn: {
    marginLeft: theme.spacing(1),
  },
  pagination: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  clearFiter: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    marginLeft: 10,
    fontWeight: 400,
  },
  backToList: {
    cursor: "pointer",
    color: COLORS.blue,
    paddingRight: 10,
    borderRight: `1px solid ${COLORS.darks[0][25]}`,
  },
  backBlock: {
    display: "flex",
    alignItems: "center",
  },
}));

const defaultFilters = {
  electricity_status: ANY_FILTER_KEY,
  expired_date: ANY_FILTER_KEY,
  square: ANY_FILTER_KEY,
};

export const TenantsList = ({ id, setShowTenantList, onSelected }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [filters, setFilters] = useState(defaultFilters);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [tenantFilters, setTenantFilters] = useState(TENANTS_FILTERS(user?.unit_system));
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    getData(`/praas/v2/buildings/${id}/tenants/`, setTenants);

    return () => setTenants([]);
  }, [id, cluster, segments, selectedCountries]);

  const filtersCount = useMemo(() => {
    const thisYear = getYear();
    const count = {
      this_q: 0,
      this_year: 0,
      square_large: 0,
      square_mid: 0,
      square_small: 0,
    };
    for (const tenant of tenants) {
      if (tenant.electricity_status?.status) {
        count[tenant.electricity_status.status] = count[tenant.electricity_status.status] || 0;
        count[tenant.electricity_status.status] += 1;
      }

      if (thisYear === getYear(new Date(tenant.leasing_end))) {
        count.this_year += 1;
      } else if (thisYear < getYear(new Date(tenant.leasing_end))) {
        count.this_q += 1;
      }

      if (tenant.total_square < (user?.has_sqm_unit ? 30 : 2500)) {
        count.square_small += 1;
      } else if (
        tenant.total_square >= (user?.has_sqm_unit ? 30 : 2500) &&
        tenant.total_square < (user?.has_sqm_unit ? 40 : 5000)
      ) {
        count.square_mid += 1;
      } else if (tenant.total_square >= (user?.has_sqm_unit ? 40 : 5000)) {
        count.square_large += 1;
      }
    }
    return count;
  }, [tenants]);

  const filterTenants = useMemo(() => {
    return tenants.filter((tenant) => {
      if (
        searchText !== "" &&
        !tenant.tenant.name.toLowerCase().includes(searchText.toLowerCase())
      ) {
        return false;
      }

      if (
        filters.electricity_status !== ANY_FILTER_KEY &&
        tenant.electricity_status?.status !== filters.electricity_status
      ) {
        return false;
      }

      if (
        filters.expired_date === "this_year" &&
        getYear() !== getYear(new Date(tenant.leasing_end))
      ) {
        return false;
      }

      if (filters.expired_date === "this_q" && getYear() >= getYear(new Date(tenant.leasing_end))) {
        return false;
      }

      if (
        filters.square === "square_large" &&
        tenant.total_square < (user?.has_sqm_unit ? 40 : 5000)
      ) {
        return false;
      }

      if (
        filters.square === "square_mid" &&
        ((user?.has_sqm_unit ? 30 : 2500) > tenant.total_square ||
          tenant.total_square >= (user?.has_sqm_unit ? 40 : 5000))
      ) {
        return false;
      }

      if (
        filters.square === "square_small" &&
        tenant.total_square >= (user?.has_sqm_unit ? 30 : 2500)
      ) {
        return false;
      }

      return true;
    });
  }, [tenants, filters, searchText]);

  const tableData = useMemo(() => {
    const items = filterTenants.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

    const res = items.map((item, i) => {
      const mpan = item.mpan.join(", ");
      return [
        item.tenant?.name,
        item.unit_id?.length > 20 ? (
          <Tooltip title={item.unit_id}>
            <Typography>{`${item.unit_id.slice(0, 5)}...`}</Typography>
          </Tooltip>
        ) : (
          <Typography style={{ wordBreak: "break-all" }}>{item.unit_id}</Typography>
        ),
        nFormatter(item.total_square),
        <Box key={i}>
          <Typography
            style={{
              color: {
                CONNECTED: COLORS.green,
                READY_TO_CONNECT: COLORS.purple,
                STAND_BY: COLORS.darks[0][50],
              }[item.electricity_status?.status],
            }}
          >
            {STATUS_MAP[item.electricity_status?.status]}
          </Typography>
          {item.electricity_status?.status === "CONNECTED" ? (
            <Typography key={i}>
              Since {formatDatetime(item.electricity_status.since_connected, "MMM D, YYYY")}
            </Typography>
          ) : null}
        </Box>,
        <div key={i}>
          {mpan.length > 15 && mpan.length > 1 ? (
            <Tooltip title={mpan}>
              <div>{mpan.substring(0, 15) + "..."}</div>
            </Tooltip>
          ) : (
            mpan
          )}
        </div>,
        item.account_number,
        item.total_rent !== null ? currencyFormatter(item.total_rent, currencySymbol) : "No Data",
        formatDatetime(item.leasing_start, "MMM D, YYYY"),
        formatDatetime(item.leasing_end, "MMM D, YYYY"),
      ];
    });
    return res;
  }, [page, filterTenants]);

  const onOptionSelect = (filterKey, optionKey) => {
    const newFilters = { ...filters, [filterKey]: optionKey };
    setFilters(newFilters);
    setPage(0);
  };

  return (
    <Grid className={classes.root} container>
      <Grid style={{ marginBottom: 40 }} container justifyContent="space-between">
        <Typography style={{ fontWeight: "bold" }}>
          TENANTS LIST ({tenants.length})
          <Typography
            component="span"
            className={classes.clearFiter}
            onClick={() => setFilters(defaultFilters)}
          >
            Clear filters
          </Typography>
        </Typography>
        <Box className={classes.backBlock}>
          <Typography onClick={() => setShowTenantList(false)} className={classes.backToList}>
            Back to Property data
          </Typography>
          <ActionButtonsGroup hideIntegrateBtn />
        </Box>
      </Grid>
      {tenants.length ? (
        <>
          <Grid item xs={2}>
            <TableFilter
              count={tenants.length}
              filters={filters}
              dataFilters={tenantFilters}
              filtersCount={filtersCount}
              onOptionSelect={onOptionSelect}
              setDataFilters={setTenantFilters}
            />
          </Grid>

          <Grid item xs={10}>
            <Grid className={classes.searchContainer} container>
              <UnderscoreTextInput
                value={searchText}
                onChange={setSearchText}
                placeHolder="Search for tenant..."
                width="50%"
              />
              <Icon root={classes.searchBtn} src={SearchIcon} hoverable />
            </Grid>

            <Table
              headers={[
                "Tenant Name",
                "Rented unit",
                `Total ${user.unit_system}`,
                "Electricity status",
                "MPAN",
                "Account",
                "Rental/Mon",
                "Start Date",
                "Exp. Date",
              ]}
              data={tableData}
              colWidths={[10.5, 10.5, 6, 10.5, 18.5, 8.5, 8.5, 10, 10, 5]}
              endIcon={EditIcon}
              endIconWidth="auto"
              cellPaddingRight={0}
              cellStyles={[{ padding: "18px 0 18px 20px" }]}
              maxHeight={1000}
              onEndIconClick={(rowIndex) =>
                onSelected(filterTenants[page * ITEMS_PER_PAGE + rowIndex])
              }
            />
            <Pagination
              root={classes.pagination}
              totalPage={Math.ceil(filterTenants.length / ITEMS_PER_PAGE)}
              onChange={setPage}
              page={page}
            />
          </Grid>
        </>
      ) : (
        <EmptyPage headerText="tenants" footerText="Tenancy" />
      )}
    </Grid>
  );
};
